import React, { useState } from 'react';
import { usePlaybook } from '../context/PlaybookContext';
import SafeIcon from '../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';
import { formatDate } from '../utils/helpers';

const Governance = () => {
  const { governanceReports, generateGovernanceReport, aiPolicies } = usePlaybook();
  const [selectedReport, setSelectedReport] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerate = async () => {
    setIsGenerating(true);
    await generateGovernanceReport();
    setTimeout(() => setIsGenerating(false), 1000);
  };

  const handleDownload = () => {
    // Mock nedlasting
    alert("Laster ned rapport som PDF...");
  };

  const handleShare = () => {
    // Mock deling
    alert("Delingslenke er kopiert til utklippstavlen.");
  };

  const ReportSection = ({ title, icon, content }) => (
    <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
      <h4 className="flex items-center gap-2 text-sm font-black text-slate-900 uppercase tracking-widest mb-4">
        <SafeIcon icon={icon} className="w-4 h-4 text-primary-600" />
        {title}
      </h4>
      <div className="prose prose-sm text-slate-600 font-medium whitespace-pre-wrap">
        {content || 'Ingen data.'}
      </div>
    </div>
  );

  return (
    <div className="p-8 max-w-[1600px] mx-auto min-h-screen space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center border-b border-slate-200 pb-6">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight flex items-center gap-3">
            <SafeIcon icon="⚖️" /> Sales Governance
          </h1>
          <p className="text-slate-500 font-medium mt-1">ISO-sertifisert rapportering og kontroll.</p>
        </div>
        <button 
          onClick={handleGenerate} 
          disabled={isGenerating}
          className="flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-xl font-black text-xs uppercase tracking-widest hover:bg-slate-800 transition-all shadow-lg shadow-slate-200 disabled:opacity-70"
        >
          {isGenerating ? <SafeIcon icon="🔄" className="animate-spin" /> : <SafeIcon icon="📸" />}
          Generer Ny Rapport
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        {/* Sidebar: Report History */}
        <div className="lg:col-span-3 space-y-4">
          <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest px-2">Rapporthistorikk</h3>
          {governanceReports.length > 0 ? (
            <div className="space-y-2">
              {governanceReports.map(report => (
                <div 
                  key={report.report_id} 
                  onClick={() => setSelectedReport(report)}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all group ${selectedReport?.report_id === report.report_id ? 'bg-primary-50 border-primary-600 text-primary-900' : 'bg-white border-slate-200 hover:border-slate-300'}`}
                >
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-bold text-sm">ISO Audit</span>
                    <SafeIcon icon="➡️" className={`w-4 h-4 text-primary-400 transition-opacity ${selectedReport?.report_id === report.report_id ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`} />
                  </div>
                  <p className="text-xs opacity-70 font-mono">{formatDate(report.generated_at, true)}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
              <p className="text-slate-400 text-xs font-bold">Ingen rapporter enda.</p>
            </div>
          )}
        </div>

        {/* Main Content Area */}
        <div className="lg:col-span-9">
          <AnimatePresence mode="wait">
            {selectedReport ? (
              <motion.div 
                key={selectedReport.report_id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-8"
              >
                {/* Report Header */}
                <div className="bg-slate-900 text-white p-8 rounded-[2rem] shadow-xl relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-primary-600/20 rounded-full blur-3xl -mr-20 -mt-20"></div>
                  <div className="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                    <div>
                      <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 rounded-full text-[10px] font-black uppercase tracking-widest mb-4">
                        <SafeIcon icon="✅" className="text-emerald-400" /> Verifisert Snapshot
                      </div>
                      <h2 className="text-3xl font-black tracking-tight mb-2">Sales Governance Report</h2>
                      <p className="text-slate-400 text-sm font-mono">ID: {selectedReport.report_id}</p>
                    </div>
                    
                    <div className="flex flex-col items-end gap-4">
                      <div className="text-right">
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Generert dato</p>
                        <p className="text-xl font-bold">{formatDate(selectedReport.generated_at, true)}</p>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={handleShare} className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg text-xs font-bold uppercase tracking-widest transition-all flex items-center gap-2">
                          <SafeIcon icon="🔗" /> Del
                        </button>
                        <button onClick={handleDownload} className="px-4 py-2 bg-primary-600 hover:bg-primary-500 text-white rounded-lg text-xs font-bold uppercase tracking-widest transition-all shadow-lg flex items-center gap-2">
                          <SafeIcon icon="⬇️" /> Last ned PDF
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Report Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <ReportSection title="Metodikk & Prosess" icon="📖" content={selectedReport.methodology} />
                  <ReportSection title="AI Policy & Retningslinjer" icon="🤖" content={selectedReport.ai_usage_policy} />
                  <ReportSection title="Roller & Ansvar" icon="👥" content={selectedReport.roles_and_responsibility} />
                  <ReportSection title="Scenario Management" icon="🎭" content={selectedReport.scenario_management} />
                  <ReportSection title="Onboarding & Opplæring" icon="🚀" content={selectedReport.onboarding_process} />
                  
                  <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200 shadow-inner">
                    <h4 className="flex items-center gap-2 text-sm font-black text-slate-900 uppercase tracking-widest mb-4">
                      <SafeIcon icon="🔒" className="w-4 h-4 text-emerald-600" /> Audit & Sporbarhet
                    </h4>
                    <p className="text-sm text-slate-600 font-mono bg-white p-4 rounded-xl border border-slate-200">
                      {selectedReport.audit_traceability}
                    </p>
                  </div>
                </div>
              </motion.div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center p-12 bg-white rounded-[3rem] border border-slate-200 shadow-sm min-h-[500px]">
                <div className="w-24 h-24 bg-slate-50 rounded-full flex items-center justify-center mb-6">
                  <SafeIcon icon="📊" className="w-12 h-12 text-slate-300" />
                </div>
                <h3 className="text-xl font-black text-slate-900 mb-2">Velg en rapport</h3>
                <p className="text-slate-500 font-medium max-w-xs text-center">
                  Klikk på en rapport i listen til venstre for å se detaljer, eller generer en ny.
                </p>
              </div>
            )}
          </AnimatePresence>
        </div>

      </div>
    </div>
  );
};

export default Governance;